import { Badge, BadgeTone } from "@/components/ui/badge";
import { SimpleTooltip } from "@/components/ui/simple-tooltip";
import { ScenarioSummary } from "@/types/dialer/workflow";
import { formatParis } from "@/utils/dialer-datetime";

export type ScenarioPillState = {
    label: string;
    /** Design-system tone of the verdict, mapped here until the server sends it with the summary. */
    tone: BadgeTone;
    /** A version whose scenarios fail cannot be deployed. */
    failing: boolean;
    /** Deployable, but the operator should be told. */
    warning: boolean;
    tooltip: string;
};

/**
 * The pill shown on the lineage, on the version and inside the deploy dialog. It is
 * advisory: whether a deployment is allowed is decided by `deploy-preview.blockers`,
 * so the interface stays the readable rampart and never the only one.
 */
export function scenarioPillState(summary: ScenarioSummary | null | undefined): ScenarioPillState {
    if (!summary || summary.total === 0) {
        return { label: "no scenario", tone: "warning", failing: false, warning: true, tooltip: "Nothing proves this version behaves as intended." };
    }
    if (summary.failed > 0) {
        return { label: `${summary.failed} failed`, tone: "danger", failing: true, warning: false, tooltip: `${summary.failed} of ${summary.total} scenarios fail on this version.` };
    }
    if (summary.stale) {
        return {
            label: `${summary.passed}/${summary.total} stale`,
            tone: "warning",
            failing: false,
            warning: true,
            tooltip: "Scenarios changed since the last run: replay them to trust this result."
        };
    }
    return {
        label: `${summary.passed}/${summary.total} passed`,
        tone: "success",
        failing: false,
        warning: false,
        tooltip: summary.passed_at ? `Last run ${formatParis(summary.passed_at)}` : "All scenarios pass."
    };
}

/**
 * The pill states a verdict and offers nothing to do with it: its tooltip restates the
 * label it already shows, and whether that verdict allows a deployment is answered by
 * `deploy-preview`. So it is a display, and `asChild` hands the tooltip trigger to a plain
 * `span` instead of the `button` Radix renders by default.
 *
 * That is not a matter of markup validity alone. The pill is rendered inside the lineage
 * row, which is itself a button, and a button inside a button is a hole: the browser
 * refuses to nest them, so a click on the pill selected no version at all — over the most
 * readable part of the row, which is exactly where a hand aims. Handing the trigger to a
 * `span` keeps the hover explanation and lets the click reach the row underneath.
 *
 * A `span` is not focusable, so the sentence is no longer reachable by keyboard here. The
 * row carries it as an accessible description instead, which is the right owner: a
 * keyboard user tabs to a version, not to a badge on it.
 */
export function ScenarioPill({ summary }: { summary: ScenarioSummary | null | undefined }) {
    const state = scenarioPillState(summary);
    return (
        <SimpleTooltip message={state.tooltip} asChild>
            <span className="inline-flex">
                <Badge tone={state.tone} data-testid="scenario-pill">
                    {state.label}
                </Badge>
            </span>
        </SimpleTooltip>
    );
}
